// Global Instructions Rule Applied!
//
// The three proposal verbs — review (approve / reject), retry and undo —
// shared by the two screens that offer them:
//   * My Agents → Agent activity  (the user's own rows)
//   * Agent Oversight             (every user's rows, app admins only)
//
// There is no admin-specific endpoint. Both screens post to the same
// /agent/proposals routes, and the RPCs behind them decide whether the caller
// may act (owner OR is_app_admin). That is deliberate: an admin assisting a
// user runs the identical claim → amend → replay → finalize sequence, so the
// two paths cannot drift in what they create, what they record, or what they
// report.
//
// The caller supplies `refresh` — its own list reload — so the hook never
// needs to know which screen it is on.
//
// approve replays the proposal's stored payload through the REAL write path
// (process_new_deal and friends) backend-side, as the ACTING human; reject
// marks the row rejected and creates nothing. undo reverses an applied write
// through the module's own delete path: the toast follows what the backend
// actually did — a clean undo is a success, a cluster with members left in
// place is a warning naming the count (the row lists them), and a refused
// primary record is an error.
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { dismissErrorToast, showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  reviewAgentProposal,
  retryAgentProposal,
  undoAgentWrite
} from '../services/agentAccessService.ts'

// One error toast per proposal: a retry that succeeds takes down the failure
// toast its predecessor raised; a repeat failure updates it instead of
// stacking.
const proposalToastKey = (proposalId: string) => `agent-proposal:${proposalId}`

export interface ProposalActionsApi {
  /** The proposal a verb is currently running against, or null. */
  reviewingId: string | null
  review: (
    proposalId: string,
    action: 'approve' | 'reject',
    payload?: Record<string, unknown> | null
  ) => Promise<boolean>
  retry: (proposalId: string, payload?: Record<string, unknown> | null) => Promise<boolean>
  undo: (proposalId: string) => Promise<boolean>
}

export const useProposalActions = (refresh: () => Promise<void>): ProposalActionsApi => {
  const [reviewingId, setReviewingId] = useState<string | null>(null)

  // `payload` is the record the approver edited in the review form. Passing it
  // through unchanged means approve-as-filed and approve-as-corrected are the
  // same call, so their feedback and refresh behaviour cannot drift.
  const review = useCallback(
    async (
      proposalId: string,
      action: 'approve' | 'reject',
      payload: Record<string, unknown> | null = null
    ): Promise<boolean> => {
      setReviewingId(proposalId)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await reviewAgentProposal(proposalId, action, token, payload)
        if (!res.success) {
          showErrorToast(res.error || 'Failed to review proposal')
          return false
        }
        if (action === 'reject') {
          dismissErrorToast(proposalToastKey(proposalId))
          toast.success('Proposal rejected — nothing was created')
        } else if (res.status === 'applied') {
          dismissErrorToast(proposalToastKey(proposalId))
          toast.success('Proposal approved — the record has been created')
        } else {
          // Approved but the replay failed. Name the reason here — the row
          // shows the same text (plus duplicates / outcome JSON) after refresh.
          const reason =
            typeof res.result?.error === 'string' && res.result.error.trim()
              ? res.result.error.trim()
              : 'no error text was recorded'
          showErrorToast(`Approved, but creating the record failed: ${reason}`, {
            key: proposalToastKey(proposalId)
          })
        }
        await refresh()
        return true
      } finally {
        setReviewingId(null)
      }
    },
    [refresh]
  )

  // Re-approve a failed row, optionally with the payload CORRECTED in the
  // review form — the usual case, since replaying a record that failed on its
  // own contents fails the same way. Same feedback rule as approve.
  const retry = useCallback(
    async (
      proposalId: string,
      payload: Record<string, unknown> | null = null
    ): Promise<boolean> => {
      setReviewingId(proposalId)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await retryAgentProposal(proposalId, token, payload)
        if (!res.success) {
          showErrorToast(res.error || 'Failed to retry proposal')
          return false
        }
        if (res.status === 'applied') {
          // The condition the earlier failure toast reported has cleared.
          dismissErrorToast(proposalToastKey(proposalId))
          toast.success('Retried — the record has been created')
        } else {
          const reason =
            typeof res.result?.error === 'string' && res.result.error.trim()
              ? res.result.error.trim()
              : 'no error text was recorded'
          showErrorToast(`Retried, but creating the record failed again: ${reason}`, {
            key: proposalToastKey(proposalId)
          })
        }
        await refresh()
        return true
      } finally {
        setReviewingId(null)
      }
    },
    [refresh]
  )

  const undo = useCallback(
    async (proposalId: string): Promise<boolean> => {
      setReviewingId(proposalId)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await undoAgentWrite(proposalId, token)
        if (!res.success) {
          showErrorToast(res.error || 'Failed to undo')
          // The claim may well have gone through (the row is now 'undone'
          // with result.undo_error) even though nothing was removed — show
          // that state rather than a stale Undo button.
          await refresh()
          return false
        }
        const residue = res.residue ?? []
        if (residue.length > 0) {
          toast.warning(
            `Undone, but ${residue.length} record${residue.length === 1 ? ' was' : 's were'} left in place — see the row for details`
          )
        } else {
          toast.success('Undone — the records have been removed')
        }
        await refresh()
        return true
      } finally {
        setReviewingId(null)
      }
    },
    [refresh]
  )

  return { reviewingId, review, retry, undo }
}
